import React, { createContext, useContext, useMemo, useState } from 'react';
import { nanoid } from 'nanoid';

type ToastTone = 'info' | 'success' | 'error';

interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
}

interface ToastContextValue {
  pushToast: (message: string, tone?: ToastTone, durationMs?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const pushToast = (message: string, tone: ToastTone = 'info', durationMs = 3500) => {
    const id = nanoid();
    setToasts((prev) => [...prev, { id, message, tone }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, durationMs);
  };

  const value = useMemo(() => ({ pushToast }), []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 top-20 z-50 flex w-72 flex-col gap-2 text-sm">
        {toasts.map((toast) => {
          const toneStyles =
            toast.tone === 'success'
              ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100'
              : toast.tone === 'error'
                ? 'border-rose-500/60 bg-rose-500/20 text-rose-100'
                : 'border-white/40 bg-white/10 text-white';
          return (
            <div
              key={toast.id}
              className={`pointer-events-auto rounded-xl border px-4 py-3 shadow-lg shadow-black/30 backdrop-blur ${toneStyles}`}
            >
              {toast.message}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
